import { EnvConfig } from '../config';
import { Localizations, localizations, parseLocalizations } from '../i18n';
import { asCmsLang, mergeArray, mergeDeep } from '../utils';
import { httpRequest } from './httpUtils';
import { Lock, Response } from './types';

const TRANSLATIONS_PROPS = 'localSettings,jsonUrl';
const BOOKING_PROPS = 'localSettings,url,picture,responsiveImageData,responsiveImageLinksData&nestingDepth=1';
const lock: Lock = new Lock();

// == Interfaces ===============================================================

export interface TranslationsResponse {
  data: any;
}

export interface SearchResult {
  teaserText: string;
  teaserTitle: string;
}

export interface SearchResults {
  hits: SearchResult[];
}

export interface PromoCodeTerms {
  terms: string;
  title: string;
}

// == Services =================================================================

const getBundle = (
  cmsUrl: string,
  locale: string,
  bundle: string,
  properties: string
): Promise<TranslationsResponse> => {
  const lang: string = asCmsLang(locale);
  const baseUrl = `${cmsUrl}/service/json/v1/paths/${lang}/${bundle}?properties=${properties}`;
  return httpRequest({
    method: 'GET',
    url: baseUrl,
    timeout: 3000,
    retries: 2,
    lock,
  })
    .then(({ payload }: Response<any>) => ({ data: { ...payload.localSettings, picture: payload.picture } }))
    .catch(() => ({ data: {} }));
};

const getSearch = (
  cmsUrl: string,
  locale: string,
  documentType: string,
  propertySet: string,
  subjectTaxonomyExtRef: string,
  segment: string
): Promise<SearchResults> => {
  const lang: string = asCmsLang(locale);
  const baseUrl = `${cmsUrl}/service/json/v1/search/${lang}?documenttype=${documentType}&locale=${locale}&propertySet=${propertySet}&subjectTaxonomyExtRef=${subjectTaxonomyExtRef}&segment=${segment}`;
  return httpRequest({
    method: 'GET',
    url: baseUrl,
    timeout: 3000,
    retries: 2,
    lock,
  })
    .then(({ payload }: Response<any>) => payload)
    .catch(() => ({ hits: [] }));
};

export const getLocalizations = ({ cmsUrl }: EnvConfig, locale: string): Promise<Localizations> => {
  lock.cancel();

  return Promise.all([
    getBundle(cmsUrl, locale, 'translations', TRANSLATIONS_PROPS),
    getBundle(cmsUrl, locale, 'booking', BOOKING_PROPS),
  ])
    .then(([global, booking]: TranslationsResponse[]) =>
      parseLocalizations(mergeDeep([global.data, booking.data], { overwriteWithEmpty: false, arrayMerge: mergeArray }))
    )
    .catch(() => localizations)
    .finally(() => lock.release());
};

export const getPromoCodeTerms = (
  { cmsUrl }: EnvConfig,
  locale: string,
  promoCode: string
): Promise<PromoCodeTerms | undefined> => {
  return getSearch(
    cmsUrl,
    locale,
    'CMArticle',
    'finnaircom',
    'Subject/discountCodeTerms',
    promoCode.toLowerCase()
  ).then((response) =>
    response.hits?.length ? { terms: response.hits[0].teaserText, title: response.hits[0].teaserTitle } : undefined
  );
};
